import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../utils/api';
import { useTheme } from '../hooks/useTheme';
import { useToast } from '../contexts/ToastContext';
import useDeferredLoading from '../hooks/useDeferredLoading';
import useAutosave, {
  AUTOSAVE_DIRTY,
  AUTOSAVE_ERROR,
  AUTOSAVE_HELD,
  AUTOSAVE_SAVING,
} from '../hooks/useAutosave';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import QrModal from '../components/modals/QrModal';
import VeloxCard from '../components/card/VeloxCard';
import AddPhysicalCardDialog from '../components/account/AddPhysicalCardDialog';
import { activateCard, activationErrorMessage } from '../utils/cardActivation';
import styles from '../styles/MyCardPage.module.css';

/*
 * My Card: the editor for the one card the account publishes.
 *
 * There is no save button. Edits are committed by hooks/useAutosave a short
 * pause after typing stops and immediately on blur, which suits this form —
 * eight independent text fields, no destructive actions, and an endpoint that
 * replaces the whole card document, so a repeated write is harmless. The button
 * it replaced only appeared once the form was dirty and sat below the last
 * field, which on a phone meant the one control that protected the user's work
 * was off screen at the moment they had unsaved work.
 *
 * Because a save fans out to every contact who has already saved this card
 * (see Contact.updateMany in controllers/userController.js), a malformed value
 * would propagate. Autosave therefore withholds a payload while any field is
 * invalid rather than writing it and reporting a problem afterwards.
 *
 * The physical card list lives on Account. This page edits the shared profile.
 */

/** Matches the card sub-document in models/User.js, in a fixed key order so the
 *  autosave baseline comparison is stable. */
const CARD_FIELDS = [
  'title',
  'name',
  'company',
  'role',
  'email',
  'phone',
  'linkedinURL',
  'additionalURL',
];

const EMPTY_CARD = CARD_FIELDS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

/*
 * Grouped in the order the card reads: who you are, then how to reach you, then
 * where else to find you. Only the two fields the card cannot be published
 * without are marked; labelling the other six "optional" put a tag on the
 * majority of the form and said less than marking the minority does.
 */
const FIELD_GROUPS = [
  {
    id: 'identity',
    title: 'Your Details',
    fields: [
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        placeholder: 'Mr, Ms, Dr',
        autoComplete: 'honorific-prefix',
      },
      {
        name: 'name',
        label: 'Full name',
        type: 'text',
        placeholder: 'Your full name',
        autoComplete: 'name',
        required: true,
      },
      {
        name: 'company',
        label: 'Company',
        type: 'text',
        placeholder: 'Where you work',
        autoComplete: 'organization',
      },
      {
        name: 'role',
        label: 'Job title',
        type: 'text',
        placeholder: 'What you do there',
        autoComplete: 'organization-title',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    fields: [
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        inputMode: 'email',
        placeholder: 'you@company.com',
        autoComplete: 'email',
        required: true,
      },
      {
        name: 'phone',
        label: 'Phone',
        type: 'tel',
        inputMode: 'tel',
        placeholder: '+44 7700 900123',
        autoComplete: 'tel',
        hint: 'Include your country code so the number still works abroad.',
      },
    ],
  },
  {
    id: 'links',
    title: 'Links',
    fields: [
      {
        name: 'linkedinURL',
        label: 'LinkedIn',
        type: 'url',
        inputMode: 'url',
        placeholder: 'linkedin.com/in/yourname',
        autoComplete: 'url',
      },
      {
        name: 'additionalURL',
        label: 'Website',
        type: 'url',
        inputMode: 'url',
        placeholder: 'yourcompany.com',
        autoComplete: 'url',
      },
    ],
  },
];

const FIELD_LABELS = FIELD_GROUPS.reduce((acc, group) => {
  group.fields.forEach((field) => {
    acc[field.name] = field.label;
  });
  return acc;
}, {});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Deliberately lenient: VeloxCard adds the scheme, so a bare host is valid. */
const URL_PATTERN = /^(https?:\/\/)?[^\s/?#.]+(\.[^\s/?#.]+)+([/?#]\S*)?$/i;

/**
 * Only the problems worth withholding a write for. Emptiness counts for the two
 * fields the card cannot do without, because a save propagates to every contact
 * who has already saved this card and would blank the same field for all of them.
 */
function findProblems(card) {
  const problems = {};
  if (!String(card.name || '').trim()) {
    problems.name = 'Add your name — your card cannot be shared without one.';
  }
  const email = String(card.email || '').trim();
  if (!email) {
    problems.email = 'Add an email address so new contacts can reach you.';
  } else if (!EMAIL_PATTERN.test(email)) {
    problems.email = 'This does not look like an email address, e.g. you@company.com';
  }
  const linkedin = String(card.linkedinURL || '').trim();
  if (linkedin && !URL_PATTERN.test(linkedin)) {
    problems.linkedinURL = 'Enter a web address, e.g. linkedin.com/in/yourname';
  }
  const website = String(card.additionalURL || '').trim();
  if (website && !URL_PATTERN.test(website)) {
    problems.additionalURL = 'Enter a web address, e.g. yourcompany.com';
  }
  return problems;
}

function normaliseCard(source) {
  const card = source || {};
  return CARD_FIELDS.reduce(
    (acc, key) => ({ ...acc, [key]: typeof card[key] === 'string' ? card[key] : '' }),
    {}
  );
}

/** Fixed key order, so two equal cards always serialise identically. */
function serializeCard(card) {
  return JSON.stringify(CARD_FIELDS.map((key) => String(card?.[key] ?? '').trim()));
}

function toPayload(card) {
  return CARD_FIELDS.reduce((acc, key) => ({ ...acc, [key]: String(card?.[key] ?? '').trim() }), {});
}

/* ---------- Local draft, so a refresh or a dropped connection loses nothing ---------- */

const DRAFT_VERSION = 1;
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function draftStorageKey(userId) {
  return `velox:my-card-draft:${userId || 'anon'}`;
}

function readDraft(userId) {
  try {
    const raw = window.localStorage.getItem(draftStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== DRAFT_VERSION || !parsed.card) return null;
    // An edit nobody came back to within a day is likelier to be stale than wanted.
    if (!parsed.at || Date.now() - parsed.at > DRAFT_MAX_AGE_MS) return null;
    return normaliseCard(parsed.card);
  } catch {
    return null;
  }
}

function writeDraft(userId, card) {
  try {
    window.localStorage.setItem(
      draftStorageKey(userId),
      JSON.stringify({ v: DRAFT_VERSION, at: Date.now(), card: toPayload(card) })
    );
  } catch {
    // Private browsing or a full quota: autosave still works, only the
    // refresh-proofing is lost.
  }
}

function clearDraft(userId) {
  try {
    window.localStorage.removeItem(draftStorageKey(userId));
  } catch {
    // As above.
  }
}

/* ---------- Icons ---------- */

const strokeProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

const IconLink = ({ size = 15 }) => (
  <svg {...strokeProps} width={size} height={size}>
    <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L12 4" />
    <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07L12 20" />
  </svg>
);

const IconQr = ({ size = 15 }) => (
  <svg {...strokeProps} width={size} height={size}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3z" />
    <path d="M20 20h1" />
    <path d="M14 20h1" />
    <path d="M20 14h1" />
  </svg>
);

const IconCheck = ({ size = 14 }) => (
  <svg {...strokeProps} width={size} height={size} strokeWidth={2.2}>
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

const IconAlert = ({ size = 14 }) => (
  <svg {...strokeProps} width={size} height={size}>
    <path d="M12 3.5 21 19H3z" />
    <path d="M12 9.5v4" />
    <path d="M12 16.5h.01" />
  </svg>
);

const IconClose = ({ size = 14 }) => (
  <svg {...strokeProps} width={size} height={size}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </svg>
);

/* ---------- Save status ---------- */

/**
 * One line of plain language per state, in a polite live region. The status only
 * changes once per burst of typing, so it does not chatter at a screen reader.
 */
function describeSave({ status, savedAt, problemFields, offline }) {
  if (status === AUTOSAVE_SAVING) return { tone: 'busy', text: 'Saving…' };
  if (status === AUTOSAVE_HELD) {
    // Naming the field saves the user hunting for it, since the status can be
    // pinned some way from the input that is holding things up.
    let which = 'the highlighted fields';
    if (problemFields.length === 1) which = problemFields[0];
    else if (problemFields.length === 2) which = `${problemFields[0]} and ${problemFields[1]}`;
    else if (problemFields.length > 2) which = `${problemFields.length} fields`;
    return { tone: 'alert', text: `Not saved — check ${which}` };
  }
  if (status === AUTOSAVE_ERROR) {
    return {
      tone: 'alert',
      retry: true,
      text: offline
        ? 'No connection. Your changes are kept and will save when you are back online.'
        : 'Your changes could not be saved.',
    };
  }
  if (status === AUTOSAVE_DIRTY) return { tone: 'muted', text: 'Unsaved changes' };
  if (savedAt) return { tone: 'saved', text: 'All changes saved' };
  return { tone: 'muted', text: 'Changes save automatically' };
}

function SaveStatus({ status, savedAt, problemFields, pinned, onRetry }) {
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const state = describeSave({ status, savedAt, problemFields, offline });
  const alert = state.tone === 'alert';

  return (
    <div
      className={[
        styles.statusBar,
        pinned ? styles.statusBarPinned : '',
        alert ? styles.statusBarAlert : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p
        className={[
          styles.statusText,
          state.tone === 'saved' ? styles.statusTextSaved : '',
          alert ? styles.statusTextAlert : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="status"
        aria-live="polite"
      >
        {state.tone === 'busy' ? (
          <span className={styles.spinner} aria-hidden="true" />
        ) : state.tone === 'saved' ? (
          <span className={`${styles.statusIcon} ${styles.statusIconSaved}`}>
            <IconCheck />
          </span>
        ) : alert ? (
          <span className={styles.statusIcon}>
            <IconAlert />
          </span>
        ) : null}
        {state.text}
      </p>

      {state.retry ? (
        <button type="button" className={styles.retryButton} onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/* ---------- Loading ---------- */

function CardSkeleton() {
  return (
    <div className={styles.page}>
      <span className={styles.visuallyHidden}>Loading your card</span>
      <div className={styles.layout} aria-hidden="true">
        <div className={styles.aside}>
          <div>
            <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
            <div className={`${styles.skeleton} ${styles.skeletonCard}`} />
          </div>
        </div>
        <div className={styles.editor}>
          {[0, 1].map((panel) => (
            <div key={panel} className={styles.panel}>
              <div className={styles.panelHead}>
                <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
              </div>
              <div className={styles.fields}>
                {[0, 1].map((field) => (
                  <div key={field} className={styles.field}>
                    <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
                    <div className={`${styles.skeleton} ${styles.skeletonField}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

/** Canonical public origin for sharing and QR — never window.location.origin. */
const PUBLIC_ORIGIN = 'https://velox-network.com';

const MyCardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  useTheme();
  const { showWarning, showSuccess } = useToast();

  const [card, setCard] = useState(EMPTY_CARD);
  const [problems, setProblems] = useState({});
  const [accountId, setAccountId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const showSkeleton = useDeferredLoading(isLoading, 250);
  const [showActivationBanner, setShowActivationBanner] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [isWalletBusy, setIsWalletBusy] = useState(false);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();
  const [hasActivePhysical, setHasActivePhysical] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showAppleWallet, setShowAppleWallet] = useState(false);

  /** The live draft, so handlers never read a stale card out of a closure. */
  const cardRef = useRef(EMPTY_CARD);
  const userIdRef = useRef(null);

  const saveCard = useCallback(async (next) => {
    // The endpoint replaces the whole sub-document, so every field goes every
    // time; sending a subset would blank the rest.
    await api.post('/api/user/updateBusinessCard', toPayload(next));
  }, []);

  const {
    status,
    savedAt,
    hasUnsavedChanges,
    setBaseline,
    change,
    flush,
    retry,
  } = useAutosave({ save: saveCard, delay: 900, serialize: serializeCard });

  const applyCard = useCallback(
    (next) => {
      cardRef.current = next;
      setCard(next);
    },
    []
  );

  /** Records an edit: keeps a local copy for the next page load, then queues it. */
  const queueSave = useCallback(
    (next) => {
      const found = findProblems(next);
      const outcome = change(next, { hold: Object.keys(found).length > 0 });
      // Typing something and then undoing it leaves nothing to rescue, so the
      // local copy goes too — otherwise the next visit would offer to restore an
      // edit the user had already abandoned.
      if (outcome === 'clean') clearDraft(userIdRef.current);
      else writeDraft(userIdRef.current, next);
    },
    [change]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchUserData = async () => {
      try {
        const response = await api.get('/api/user/account');
        if (cancelled) return;
        const userData = response.data || {};
        const server = normaliseCard(userData.businessCard);

        userIdRef.current = userData._id || null;
        if (userData._id) setAccountId(String(userData._id));
        setHasActivePhysical(
          Array.isArray(userData.physicalCards)
          && userData.physicalCards.some((item) => item.status === 'active')
        );
        setBaseline(server);

        // An edit this device never managed to persist — a refresh mid-save, or
        // a save attempted while offline — outranks the server's copy, because
        // it is the more recent thing this user actually typed.
        const draft = readDraft(userIdRef.current);
        if (draft && serializeCard(draft) !== serializeCard(server)) {
          applyCard(draft);
          setRestoredDraft(true);
          queueSave(draft);
        } else {
          applyCard(server);
          clearDraft(userIdRef.current);
        }
      } catch (error) {
        console.error('Error fetching user card data:', error);
        if (!cancelled) showWarning('We could not load your card. Check your connection and refresh.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUserData();
    return () => {
      cancelled = true;
    };
    // These three are stable, so the fetch runs once. It deliberately no longer
    // shares an effect with the activation handlers below: re-running it when
    // the query string changed used to reset the form under the user.
  }, [applyCard, queueSave, setBaseline, showWarning]);

  // Apple Wallet badge: phones/tablets, and narrow viewports (DevTools mobile).
  // Do not require pointer:coarse — Chrome device mode keeps a fine pointer.
  useEffect(() => {
    // matchMedia is absent in jsdom and in any non-browser render, and calling it
    // unguarded took the whole page down rather than just hiding the badge.
    const mq =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 820px)')
        : null;

    const sync = () => {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const mobileUa =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
        (typeof navigator !== 'undefined' &&
          navigator.platform === 'MacIntel' &&
          navigator.maxTouchPoints > 1);
      setShowAppleWallet(Boolean(mobileUa || mq?.matches));
    };
    sync();

    if (!mq) return undefined;
    const onChange = () => sync();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Card activation, unchanged in behaviour: a card can arrive here from a tap
  // link, or be waiting in localStorage after a detour through login.
  useEffect(() => {
    const handleDirectActivation = async () => {
      const cardIdToActivate = searchParams.get('activateCard');
      const token = localStorage.getItem('token');

      if (cardIdToActivate && token) {
        setIsActivating(true);
        try {
          await activateCard(cardIdToActivate);
          setHasActivePhysical(true);
          setShowActivationBanner(true);
          navigate('/my-card', { replace: true });
          showSuccess('Card activated successfully!');
        } catch (error) {
          console.error('Direct activation failed:', error);
          showWarning(
            activationErrorMessage(
              error,
              'Failed to activate card automatically. Please contact support or try manual activation.'
            )
          );
        } finally {
          setIsActivating(false);
        }
      } else if (cardIdToActivate && !token) {
        localStorage.setItem('activationCardId', cardIdToActivate);
        navigate('/login?returnUrl=' + encodeURIComponent('/my-card'));
      }
    };

    const handlePostLoginActivation = async () => {
      const activationCardId = localStorage.getItem('activationCardId');
      const token = localStorage.getItem('token');
      if (!activationCardId || !token) return;

      setIsActivating(true);
      try {
        await activateCard(activationCardId);
        setHasActivePhysical(true);
        setShowActivationBanner(true);
        localStorage.removeItem('activationCardId');
        showSuccess('Card activated successfully!');
      } catch (error) {
        console.error('Post-login activation failed:', error);
        showWarning(
          activationErrorMessage(error, 'Failed to activate card automatically. Please try manual activation.')
        );
      } finally {
        setIsActivating(false);
      }
    };

    if (localStorage.getItem('cardActivationSuccess') === 'true') {
      setShowActivationBanner(true);
      localStorage.removeItem('cardActivationSuccess');
    }
    handleDirectActivation();
    handlePostLoginActivation();
  }, [searchParams, navigate, showSuccess, showWarning]);

  // Once the server holds everything, the local copy has nothing left to rescue.
  useEffect(() => {
    if (!hasUnsavedChanges && savedAt) clearDraft(userIdRef.current);
  }, [hasUnsavedChanges, savedAt]);

  // The debounce has elapsed on a payload we would not send, so the fields
  // responsible can now be named. Waiting until this point is what keeps an
  // error from appearing under a field the user is still halfway through.
  useEffect(() => {
    if (status === AUTOSAVE_HELD) setProblems(findProblems(cardRef.current));
  }, [status]);

  const overlayOpen = isNavOpen || notifications.open;
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [overlayOpen]);

  const handleFieldChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      const next = { ...cardRef.current, [name]: value };
      applyCard(next);
      setRestoredDraft(false);
      // Clear a message the moment its field is acceptable again, but never add
      // one here: the value is still being typed.
      setProblems((prev) => {
        if (!prev[name]) return prev;
        if (findProblems(next)[name]) return prev;
        const rest = { ...prev };
        delete rest[name];
        return rest;
      });
      queueSave(next);
    },
    [applyCard, queueSave]
  );

  /** Leaving a field is a clear signal the value is finished. */
  const handleFieldBlur = useCallback(() => {
    setProblems(findProblems(cardRef.current));
    flush();
  }, [flush]);

  const publicUrl = useMemo(() => {
    // The share link is the account. A physical card never replaces it.
    const id = String(accountId || '').trim();
    return id ? `${PUBLIC_ORIGIN}/c/${encodeURIComponent(id)}` : '';
  }, [accountId]);

  const handleCopyLink = useCallback(async () => {
    if (!publicUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicUrl);
        showSuccess('Link copied');
        return;
      }
    } catch {
      // Fall through to the textarea route below.
    }
    try {
      const el = document.createElement('textarea');
      el.value = publicUrl;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showSuccess('Link copied');
    } catch {
      showWarning('Unable to copy link on this device.');
    }
  }, [publicUrl, showSuccess, showWarning]);

  const handleAddToAppleWallet = useCallback(async () => {
    if (!publicUrl || isWalletBusy) return;
    setIsWalletBusy(true);
    let navigatedAway = false;
    try {
      const onAppleMobile =
        /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // Safari only hands off cleanly from a real HTTPS .pkpass URL. Blob URLs
      // trigger an awkward “site is trying to show you a Wallet pass” prompt and
      // often fail the Wallet sheet. Mint a short-lived ticket and navigate.
      if (onAppleMobile) {
        const { data } = await api.post('/api/user/apple-wallet-pass/ticket');
        const url = String(data?.url || '').trim();
        if (!url) throw new Error('missing pass url');
        navigatedAway = true;
        window.location.assign(url);
        return;
      }

      const res = await api.get('/api/user/apple-wallet-pass', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.apple.pkpass' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.rel = 'noopener';
      anchor.download = 'velox-card.pkpass';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      showSuccess('Velox Card pass ready');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      const status = err?.response?.status;
      let message = 'Unable to add to Apple Wallet right now.';
      if (status === 400) {
        message = 'Link a Velox card before adding it to Apple Wallet.';
      } else if (status === 503) {
        message = 'Apple Wallet is not configured on this server yet.';
      } else if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch {
          // Keep the generic message.
        }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      showWarning(message);
    } finally {
      if (!navigatedAway) setIsWalletBusy(false);
    }
  }, [publicUrl, isWalletBusy, showSuccess, showWarning]);

  /* Which fields are holding the save up. Derived from the live card rather than
     the `problems` state, so the status is right on the render that reports it;
     `problems` lags deliberately, to keep messages off fields mid-keystroke. */
  const problemFields = useMemo(
    () => Object.keys(findProblems(card)).map((name) => FIELD_LABELS[name] || name),
    [card]
  );
  const previewName = (card.name || '').trim() || 'this person';

  const preview = (
    <div className={styles.aside}>
      <section className={styles.previewStack} aria-labelledby="vx-card-preview">
        <div className={styles.previewHead}>
          <h2 className={styles.panelTitle} id="vx-card-preview">
            Your public card
          </h2>
          <p className={styles.panelDescription}>
            What people see when they tap your card or scan your QR.
          </p>
        </div>

        <div className={styles.previewCard}>
          <VeloxCard card={card} links="static" placeholders />
        </div>

        {publicUrl ? (
          <div
            className={`${styles.shareRow}${showAppleWallet ? ` ${styles.shareRowWithWallet}` : ''}`}
          >
            <button type="button" className={styles.secondaryButton} onClick={handleCopyLink}>
              <span className={styles.buttonIcon}>
                <IconLink />
              </span>
              Copy link
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setShowQr(true)}
            >
              <span className={styles.buttonIcon}>
                <IconQr />
              </span>
              QR code
            </button>
            {showAppleWallet ? (
              <button
                type="button"
                className={styles.appleWalletButton}
                onClick={handleAddToAppleWallet}
                disabled={isWalletBusy}
                aria-label="Add to Apple Wallet"
                aria-busy={isWalletBusy}
              >
                {isWalletBusy ? (
                  <span className={styles.appleWalletBusy}>
                    <span className={styles.walletSpinner} aria-hidden="true" />
                    Preparing…
                  </span>
                ) : (
                  <img
                    className={styles.appleWalletBadge}
                    src="/badges/add-to-apple-wallet.svg"
                    alt=""
                  />
                )}
              </button>
            ) : null}
          </div>
        ) : null}

        {!hasActivePhysical ? (
          <button type="button" className={styles.physicalNudge} onClick={() => setAddCardOpen(true)}>
            <span className={styles.physicalNudgeMark} aria-hidden="true">+</span>
            <span className={styles.physicalNudgeCopy}>
              <span className={styles.physicalNudgeTitle}>Add a physical business card</span>
              <span className={styles.physicalNudgeText}>Order or link your premium metal Velox card for instant tap-to-share.</span>
            </span>
          </button>
        ) : null}

      </section>
    </div>
  );

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />

      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />

      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="My Card"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />

        <div className={homeStyles.hubContent}>
          {isLoading ? (
            // Held back briefly: the account fetch is usually quick enough that
            // a skeleton would only blink.
            showSkeleton ? <CardSkeleton /> : <div aria-hidden="true" />
          ) : (
            <div className={styles.page}>
              {isActivating ? (
                <div className={styles.notice}>
                  <span className={styles.spinner} aria-hidden="true" />
                  <div className={styles.noticeBody}>
                    <p className={styles.noticeTitle} role="status">
                      Activating your card…
                    </p>
                  </div>
                </div>
              ) : null}

              {showActivationBanner ? (
                <div className={styles.notice}>
                  <div className={styles.noticeBody}>
                    <p className={styles.noticeTitle}>Your card is active</p>
                    <p className={styles.noticeText}>
                      Fill in your details below and start sharing. Everything saves as you type.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.noticeDismiss}
                    onClick={() => setShowActivationBanner(false)}
                    aria-label="Dismiss"
                  >
                    <IconClose />
                  </button>
                </div>
              ) : null}

              {restoredDraft ? (
                <div className={styles.notice}>
                  <div className={styles.noticeBody}>
                    <p className={styles.noticeTitle}>We restored your last edits</p>
                    <p className={styles.noticeText}>
                      Changes you made before leaving had not reached us. They are back in the fields
                      below and saving now.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.noticeDismiss}
                    onClick={() => setRestoredDraft(false)}
                    aria-label="Dismiss"
                  >
                    <IconClose />
                  </button>
                </div>
              ) : null}

              <div className={styles.layout}>
                {preview}

                <div className={styles.editor}>
                  {FIELD_GROUPS.map((group) => (
                    <section
                      key={group.id}
                      className={styles.panel}
                      aria-labelledby={`vx-group-${group.id}`}
                    >
                      <div className={styles.panelHead}>
                        <h2 className={styles.panelTitle} id={`vx-group-${group.id}`}>
                          {group.title}
                        </h2>
                      </div>

                      <div className={styles.fields}>
                        {group.fields.map((field) => {
                          const problem = problems[field.name];
                          const hintId = field.hint ? `vx-hint-${field.name}` : null;
                          const errorId = problem ? `vx-error-${field.name}` : null;
                          const describedBy = [errorId, hintId].filter(Boolean).join(' ');

                          return (
                            <div key={field.name} className={styles.field}>
                              <div className={styles.fieldTop}>
                                <label
                                  className={styles.fieldLabel}
                                  htmlFor={`vx-field-${field.name}`}
                                >
                                  {field.label}
                                </label>
                                {/* Outside the label, so the field's accessible
                                    name stays exactly its label. `required` on
                                    the input carries this to a screen reader. */}
                                {field.required ? (
                                  <span className={styles.fieldTag} aria-hidden="true">
                                    Required
                                  </span>
                                ) : null}
                              </div>
                              <input
                                id={`vx-field-${field.name}`}
                                className={`${styles.input} ${problem ? styles.inputInvalid : ''}`}
                                type={field.type}
                                inputMode={field.inputMode}
                                name={field.name}
                                value={card[field.name] || ''}
                                onChange={handleFieldChange}
                                onBlur={handleFieldBlur}
                                placeholder={field.placeholder}
                                autoComplete={field.autoComplete}
                                required={field.required}
                                aria-invalid={problem ? 'true' : undefined}
                                aria-describedby={describedBy || undefined}
                                spellCheck={field.type === 'text' ? undefined : 'false'}
                              />
                              {problem ? (
                                <p className={styles.fieldError} id={errorId} role="alert">
                                  {problem}
                                </p>
                              ) : field.hint ? (
                                <p className={styles.fieldHint} id={hintId}>
                                  {field.hint}
                                </p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ))}

                  <SaveStatus
                    status={status}
                    savedAt={savedAt}
                    problemFields={problemFields}
                    pinned={hasUnsavedChanges || Boolean(savedAt)}
                    onRetry={retry}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddPhysicalCardDialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onLinked={() => setHasActivePhysical(true)}
      />

      <QrModal
        open={Boolean(showQr && publicUrl)}
        onClose={() => setShowQr(false)}
        title={`Connect with ${previewName} on Velox`}
      >
        <div className={styles.qrBody}>
          <div className={styles.qrFrame}>
            <QRCodeCanvas value={publicUrl} size={172} bgColor="#FFFFFF" fgColor="#0E0E10" />
          </div>
          <p className={styles.qrCaption}>
            Scanning this opens your card and offers to save your details.
          </p>
        </div>
      </QrModal>
    </div>
  );
};

export default MyCardPage;
